import React from "react";
import "./ghost-element.styles.scss";

import { SVG_NAMESPACE } from "../../../../constants";

const DURATION = 1000;

const BlockName = "ghost-element";
const ClassNames = {
	Block: BlockName,
	Modifiers: {},
	Elements: {
		ElementContainer: `${BlockName}__element-container`,
	},
} as const;

export type GhostElementComponent = React.FC<GhostElementProps> & {
	ClassNames: typeof ClassNames;
};

export interface GhostElementProps {
	domElement: HTMLElement;
	width: number;
	height: number;
	x: number;
	y: number;
	seed: number;
	filterId: string;
	onAnimationEnd?: (event: AnimationEvent) => void;
}

export const GhostElement = ((props: GhostElementProps) => {
	console.log("dongmt GhostElement props: ", props);
	const ghostElementRef = React.useRef<SVGSVGElement>(null);
	const foreignObjectRef = React.useRef<SVGForeignObjectElement>(null);
	React.useEffect(() => {
		if (!ghostElementRef.current) return;
		const ghostElement = ghostElementRef.current;

		const onAnimationEnd = props.onAnimationEnd ?? (() => {});
		ghostElement.addEventListener("animationend", onAnimationEnd, { once: true });
		return () => {
			ghostElement.removeEventListener("animationend", onAnimationEnd);
		};
	}, [ghostElementRef, props.onAnimationEnd]);

	React.useLayoutEffect(() => {
		if (!foreignObjectRef.current) return;

		const computedStyle = window.getComputedStyle(props.domElement);
		const clone = props.domElement.cloneNode(true) as HTMLElement;
		Array.from(computedStyle).forEach((key) =>
			clone.style.setProperty(key, computedStyle.getPropertyValue(key), "important"),
		);
		const foreignObject = foreignObjectRef.current;
		foreignObject.appendChild(clone);
		// props.domElement.style.visibility = "hidden";
		return () => {
			foreignObject.removeChild(clone);
		};
	}, [foreignObjectRef, props.domElement]);

	return (
		<svg
			ref={ghostElementRef}
			className={GhostElement.ClassNames.Block}
			width={props.width}
			height={props.height}
			style={{ left: `${props.x}px`, top: `${props.y}px` }}
			viewBox={`0 0 ${props.width} ${props.height}`}
		>
			<defs>
				{createFilter(props.filterId, Math.min(props.width, props.height), props.seed)}
			</defs>
			<g filter={`url(#${props.filterId})`}>
				<foreignObject
					ref={foreignObjectRef}
					className={GhostElement.ClassNames.Elements.ElementContainer}
					width={props.width}
					height={props.height}
				/>
			</g>
		</svg>
	);
}) as GhostElementComponent;
GhostElement.ClassNames = ClassNames;

function createFilter(id: string, smallestSide: number, baseSeed: number = 42) {
	return (
		<filter
			xmlns={SVG_NAMESPACE}
			id={id}
			x="-150%"
			y="-150%"
			width="400%"
			height="400%"
			color-interpolation-filters="sRGB"
		>
			<feTurbulence
				type="fractalNoise"
				baseFrequency="0.5"
				numOctaves="1"
				result="dustNoise"
				seed={baseSeed}
			/>
			<feComponentTransfer in="dustNoise" result="dustNoiseMask">
				<feFuncA type="linear" slope="5" intercept="0">
					<animate
						attributeName="slope"
						values="5; 2; 1; 0"
						dur={`${DURATION}ms`}
						fill="freeze"
					/>
				</feFuncA>
			</feComponentTransfer>
			<feComposite
				in="SourceGraphic"
				in2="dustNoiseMask"
				operator="in"
				result="dustySource"
			/>
			<feTurbulence
				type="fractalNoise"
				baseFrequency="0.015"
				numOctaves="1"
				result="displacementNoise1"
				seed={baseSeed + 1}
			/>
			<feTurbulence
				type="fractalNoise"
				baseFrequency="1"
				numOctaves="2"
				result="displacementNoise2"
				seed={baseSeed + 2}
			/>
			<feMerge result="combinedNoise">
				<feMergeNode in="displacementNoise1" />
				<feMergeNode in="displacementNoise2" />
			</feMerge>
			<feDisplacementMap
				in="dustySource"
				in2="combinedNoise"
				scale="0"
				xChannelSelector="R"
				yChannelSelector="G"
			>
				<animate
					attributeName="scale"
					values={`0; ${smallestSide * 3}`}
					dur={`${DURATION}ms`}
					fill="freeze"
				/>
			</feDisplacementMap>
		</filter>
	);
}
